import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import Markdown from '../../components/common/Markdown';
import { loadPageBody } from '../../common/content';
import { getPreloaded } from '../../common/contentPreload';
import PageMeta from '../../components/common/PageMeta';
import JsonLd from '../../components/common/JsonLd';
import { faqPageJsonLd } from '../../common/faq';

// Drop a leading h1 — the page renders `title` as the heading. Shared so
// the cache key (contentPreload, populated server-side pre-stripped)
// matches exactly what the client would otherwise compute.
const stripLeadingH1 = text => text.replace(/^\s*#\s+.+\n+/, '');

// Markdown-backed content page (privacy policy, license, code of conduct)
// rendered from content/pages/<file>. `titleKey` resolves to the same
// footer.* labels the nav links use, so the heading stays in sync with them.
//
// Checks contentPreload's synchronous cache first — see the comment on
// useLocalizedBody for why: useEffect never runs during renderToString, so
// without this every prerendered legal page would ship a loading skeleton.
// `faqSchema` opts a page into FAQPage JSON-LD derived from its own
// markdown (see common/faq.js). Only /faq sets it — the legal pages aren't
// question-shaped and would be invalid schema.
// `robots` forwards to PageMeta for pages that are reachable but shouldn't be
// indexed — the newsletter landing pages are reached only via a Brevo redirect
// (ocl_online#155). Omitted, PageMeta leaves the meta tag off entirely.
const ContentPage = ({ titleKey, file, faqSchema = false, robots }) => {
  const { t } = useTranslation();
  const title = t(titleKey);
  const [body, setBody] = React.useState(() => getPreloaded(file) ?? null);

  React.useEffect(() => {
    let active = true;
    const cached = getPreloaded(file);
    if (cached) {
      setBody(cached);
      return undefined;
    }
    setBody(null);
    loadPageBody(file).then(text => active && setBody(stripLeadingH1(text)));
    return () => { active = false; };
  }, [file]);

  const jsonLd = faqSchema ? faqPageJsonLd(body) : null;

  return (
    <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
      <PageMeta title={title} robots={robots} />
      {jsonLd && <JsonLd data={jsonLd} />}
      <Typography variant="h2" component="h1" sx={{ color: 'surface.dark', fontSize: { xs: '2rem', md: '2.75rem' }, mb: 4 }}>
        {title}
      </Typography>
      {body === null ? (
        <Box>
          <Skeleton height={28} />
          <Skeleton height={28} />
          <Skeleton height={28} width="60%" />
        </Box>
      ) : (
        <Markdown>{body}</Markdown>
      )}
    </Container>
  );
};

export default ContentPage;
